/** RecordingList — recorder-pen captures for the current user.
 *
 *  Lists meetings with source=manual & has_audio, ordered by recorded_at desc
 *  (server order). Each row expands to expose the audio player + transcript
 *  editor + delete. Filters by matter and "key meetings only" via query params.
 */

import { useMemo, useState } from "react";
import { Button, Empty, Select, Switch, Tag, App, Popconfirm } from "antd";
import {
  PlayCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  StarFilled,
  StarOutlined,
  UnorderedListOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import {
  useMeetings,
  useDeleteMeeting,
  useUpdateMeeting,
  useMeetingTranscript,
  useTriggerMeetingTranscription,
} from "../../api/queries";
import type { Meeting } from "../../types/meeting";
import {
  MEETING_TYPE_LABELS,
  MEETING_TYPE_OPTIONS,
} from "../../types/meeting";
import RecordingPlayer from "./RecordingPlayer";
import RecordingTranscriptEditor from "./RecordingTranscriptEditor";
import styles from "../../styles/recording.module.css";

interface RecordingListProps {
  /** Lock the list to a specific matter (used when recorder pen is opened
   *  from inside a matter detail page — currently unused, default open). */
  matterId?: string;
}

function formatRecordedAt(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

function formatDuration(seconds: number | null): string {
  if (!seconds || seconds <= 0) return "--:--";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function RecordingList({ matterId }: RecordingListProps) {
  const { message } = App.useApp();
  const [keyOnly, setKeyOnly] = useState(false);
  const [meetingType, setMeetingType] = useState<string | undefined>(undefined);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingTranscriptFor, setEditingTranscriptFor] = useState<string | null>(null);

  const params = useMemo(
    () => ({
      source: "manual" as const,
      hasAudio: true,
      matterId,
      isKeyMeeting: keyOnly || undefined,
      meetingType: meetingType as never,
      pageSize: 50,
    }),
    [matterId, keyOnly, meetingType],
  );

  const { data, isLoading, error } = useMeetings(params);
  const deleteMutation = useDeleteMeeting();
  const updateMutation = useUpdateMeeting();
  const triggerTranscription = useTriggerMeetingTranscription();

  // Poll live transcript state ONLY for the expanded meeting — avoids N
  // parallel polls when the list is long. Disabled entirely when nothing is
  // expanded (the predicate inside the hook also gates on status==='pending').
  const { data: liveTranscript } = useMeetingTranscript(expandedId, !!expandedId);

  const items: Meeting[] = data?.data ?? [];

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      message.success("录音已删除");
      if (expandedId === id) setExpandedId(null);
      if (editingTranscriptFor === id) setEditingTranscriptFor(null);
    } catch (err) {
      message.error(`删除失败：${(err as Error)?.message ?? "请稍后重试"}`);
    }
  };

  const toggleKey = async (meeting: Meeting) => {
    try {
      await updateMutation.mutateAsync({
        id: meeting.id,
        data: { isKeyMeeting: !meeting.isKeyMeeting },
      });
    } catch (err) {
      message.error(`更新失败：${(err as Error)?.message ?? "请稍后重试"}`);
    }
  };

  const handleRetrigger = async (meeting: Meeting) => {
    try {
      await triggerTranscription.mutateAsync({ id: meeting.id });
      message.success("已重新触发转写");
    } catch (err) {
      message.error(`重新转写失败：${(err as Error)?.message ?? "请稍后重试"}`);
    }
  };

  /** Decide what transcript text to show for a given meeting — if it's the
   * expanded one and we have fresher live data, prefer that. */
  const resolveTranscriptText = (meeting: Meeting): string | null => {
    if (expandedId === meeting.id && liveTranscript?.text) {
      return liveTranscript.text;
    }
    return meeting.transcriptText;
  };

  /** Resolve the effective transcript status — same priority as text. */
  const resolveTranscriptStatus = (meeting: Meeting): string => {
    if (expandedId === meeting.id && liveTranscript?.status) {
      return liveTranscript.status;
    }
    return meeting.transcriptStatus ?? "none";
  };

  const resolveTranscriptProgress = (meeting: Meeting): number | null => {
    if (expandedId === meeting.id) {
      return liveTranscript?.progress ?? null;
    }
    return meeting.transcriptionProgress;
  };

  if (isLoading) {
    return <div className={styles.listLoading}>正在加载录音…</div>;
  }

  if (error) {
    return (
      <div className={styles.listError}>
        加载失败：{(error as Error)?.message ?? "请稍后重试"}
      </div>
    );
  }

  return (
    <div className={styles.listBox}>
      <div className={styles.listFilters}>
        <Select
          allowClear
          placeholder="会议类型"
          value={meetingType}
          onChange={(v) => setMeetingType(v)}
          options={MEETING_TYPE_OPTIONS}
          style={{ minWidth: 140 }}
        />
        <div className={styles.listKeyToggle}>
          <Switch
            size="small"
            checked={keyOnly}
            onChange={setKeyOnly}
            aria-label="只看关键会议"
          />
          <span>只看关键</span>
        </div>
      </div>

      {items.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="还没有录音，开始一段新的录音吧"
          className={styles.listEmpty}
        />
      ) : (
        <ul className={styles.listItems}>
          {items.map((meeting) => {
            const expanded = expandedId === meeting.id;
            const editing = editingTranscriptFor === meeting.id;
            const effectiveText = resolveTranscriptText(meeting);
            const effectiveStatus = resolveTranscriptStatus(meeting);
            const effectiveProgress = resolveTranscriptProgress(meeting);
            const isPending = effectiveStatus === "pending";
            const isFailed = effectiveStatus === "failed";
            return (
              <li key={meeting.id} className={styles.listItem}>
                <div className={styles.listItemHeader}>
                  <button
                    type="button"
                    className={styles.listItemTitleBtn}
                    onClick={() => setExpandedId(expanded ? null : meeting.id)}
                    aria-expanded={expanded}
                  >
                    <PlayCircleOutlined className={styles.listItemIcon} />
                    <div className={styles.listItemMeta}>
                      <div className={styles.listItemTitle}>
                        {meeting.title}
                        {meeting.isKeyMeeting && (
                          <StarFilled className={styles.listItemKeyIcon} />
                        )}
                      </div>
                      <div className={styles.listItemSubtitle}>
                        {meeting.meetingType && MEETING_TYPE_LABELS[meeting.meetingType] ? (
                          <Tag className={styles.listItemTypeTag}>
                            {MEETING_TYPE_LABELS[meeting.meetingType]}
                          </Tag>
                        ) : null}
                        <span>{formatRecordedAt(meeting.recordedAt ?? meeting.createdAt)}</span>
                        <span className={styles.listItemDot}>·</span>
                        <span>{formatDuration(meeting.audioDurationSeconds)}</span>
                        {isPending && (
                          <Tag className={styles.transcriptPendingTag}>
                            <LoadingOutlined /> 转写中{effectiveProgress != null ? ` ${effectiveProgress}%` : ""}
                          </Tag>
                        )}
                        {isFailed && (
                          <Tag className={styles.transcriptFailedTag}>转写失败</Tag>
                        )}
                        {effectiveStatus === "ready" && effectiveText && (
                          <Tag className={styles.transcriptReadyTag}>已转写</Tag>
                        )}
                      </div>
                    </div>
                  </button>
                  <div className={styles.listItemActions}>
                    <Button
                      type="text"
                      size="small"
                      icon={
                        meeting.isKeyMeeting ? <StarFilled /> : <StarOutlined />
                      }
                      onClick={() => toggleKey(meeting)}
                      aria-label={
                        meeting.isKeyMeeting ? "取消关键标记" : "标记为关键会议"
                      }
                      title={meeting.isKeyMeeting ? "取消关键标记" : "标记为关键会议"}
                    />
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setExpandedId(meeting.id);
                        setEditingTranscriptFor(meeting.id);
                      }}
                      aria-label="编辑文字稿"
                      title="编辑文字稿"
                    />
                    <Popconfirm
                      title="删除该录音？"
                      description="音频文件与会话记录将一并删除，操作不可恢复。"
                      okText="删除"
                      cancelText="取消"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => handleDelete(meeting.id)}
                    >
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        aria-label="删除录音"
                        title="删除"
                      />
                    </Popconfirm>
                  </div>
                </div>
                {expanded && (
                  <div className={styles.listItemBody}>
                    {editing ? (
                      <RecordingTranscriptEditor
                        meetingId={meeting.id}
                        initialText={effectiveText}
                        pending={isPending}
                        onRetrigger={() => handleRetrigger(meeting)}
                        onClose={() => setEditingTranscriptFor(null)}
                      />
                    ) : (
                      <>
                        <RecordingPlayer meetingId={meeting.id} />
                        {effectiveText && !isPending && (
                          <div className={styles.transcriptPreview}>
                            <div className={styles.transcriptPreviewHeader}>
                              <UnorderedListOutlined />
                              <span>文字稿</span>
                              <Button
                                type="link"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => setEditingTranscriptFor(meeting.id)}
                              >
                                编辑
                              </Button>
                            </div>
                            <pre className={styles.transcriptPreviewText}>
                              {effectiveText}
                            </pre>
                          </div>
                        )}
                        {isPending && (
                          <div className={styles.transcriptPreview}>
                            <div className={styles.transcriptPreviewHeader}>
                              <UnorderedListOutlined />
                              <span>正在转写…</span>
                            </div>
                            <pre className={styles.transcriptPreviewText}>
                              {effectiveText ?? "（等待首批文字…）"}
                            </pre>
                          </div>
                        )}
                        {isFailed && (
                          <div className={styles.transcriptFailedBox}>
                            转写失败：{liveTranscript?.errorMessage ?? meeting.transcriptionError ?? "未知错误"}
                            <Button
                              type="link"
                              size="small"
                              onClick={() => handleRetrigger(meeting)}
                            >
                              重新转写
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
